/**
 * What is being charged or offered: the line table, the totals under it, and
 * the sender's notes and terms.
 *
 * The arithmetic is the server's. Every figure here is printed exactly as the
 * payload delivered it, because the PDF in the client's inbox was priced by the
 * same numbers - a page that re-added the lines in the browser could round its
 * way to a total the attachment does not show.
 *
 * The table scrolls sideways inside its own box rather than shrinking. Five
 * money columns squeezed into a phone are five unreadable columns, and a
 * horizontal scroll on the table alone keeps the page itself upright.
 */
import { Card, CardContent } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { fmtMoney } from "../../lib/format";
import type { PublicDoc } from "../../lib/publicDoc";

/** One right-aligned row in the totals block. */
function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-end gap-6 ${strong ? "font-semibold" : ""}`}>
      <dt className={strong ? "text-foreground" : "text-muted-foreground"}>{label}</dt>
      <dd className="w-32 text-right tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function TextBlock({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{body}</p>
    </div>
  );
}

export function DocLines({ doc }: { doc: PublicDoc }) {
  const t = useT();
  const { locale } = useI18n();
  const money = (cents: number) => fmtMoney(cents, doc.currency, locale);
  // The server's answer, defaulting to yes for an older API - see `showVat` on
  // `PublicDoc`. With no VAT the subtotal goes too when there is no discount
  // either: it would be the total under a second name.
  const showVat = doc.showVat !== false;
  const showSubtotal = showVat || doc.discountCents > 0;

  return (
    <Card>
      <CardContent className="pt-6">
        {doc.title ? <h1 className="mb-4 text-lg font-semibold text-foreground">{doc.title}</h1> : null}

        {/* A document with no lines is a real state - a lump-sum offer written
            entirely in its title and notes - and a table drawn for it is five
            column headings over nothing. The totals below still print. */}
        {doc.lines.length > 0 ? (
        <div className="-mx-2 overflow-x-auto px-2">
          <table className={`w-full text-sm ${showVat ? "min-w-[34rem]" : "min-w-[28rem]"}`}>
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">{t("publicDoc.line.description")}</th>
                <th className="py-2 pr-4 text-right font-medium">{t("publicDoc.line.qty")}</th>
                <th className="py-2 pr-4 text-right font-medium">{t("publicDoc.line.unitPrice")}</th>
                {showVat ? (
                  <th className="py-2 pr-4 text-right font-medium">{t("publicDoc.line.tax")}</th>
                ) : null}
                <th className="py-2 text-right font-medium">{t("publicDoc.line.sum")}</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((li, i) => (
                <tr key={`${li.description}-${i}`} className="border-b border-border/60 align-top">
                  <td className="py-2 pr-4">
                    <span className="text-foreground">{li.description}</span>
                    {li.details ? (
                      <span className="mt-0.5 block whitespace-pre-wrap text-xs text-muted-foreground">{li.details}</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-foreground">
                    {li.qty}{li.unit ? ` ${li.unit}` : ""}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-foreground">{money(li.unitPriceCents)}</td>
                  {showVat ? (
                    <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{li.taxRatePct}%</td>
                  ) : null}
                  <td className="py-2 text-right tabular-nums text-foreground">{money(li.netCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        ) : null}

        <dl className="mt-4 space-y-1.5 text-sm">
          {doc.discountCents > 0 ? (
            <TotalRow label={t("publicDoc.discount")} value={`-${money(doc.discountCents)}`} />
          ) : null}
          {showSubtotal ? (
            <TotalRow label={t("publicDoc.subtotal")} value={money(doc.subtotalCents)} />
          ) : null}
          {showVat ? <TotalRow label={t("publicDoc.vat")} value={money(doc.taxCents)} /> : null}
          <div className="flex justify-end gap-6 border-t border-border pt-2 text-base font-semibold">
            <dt className="text-foreground">{t("publicDoc.total")}</dt>
            <dd className="w-32 text-right tabular-nums text-foreground">{money(doc.totalCents)}</dd>
          </div>
          {doc.paidCents > 0 ? (
            <>
              <TotalRow label={t("publicDoc.paid")} value={`-${money(doc.paidCents)}`} />
              <TotalRow label={t("publicDoc.due")} value={money(doc.dueCents)} strong />
            </>
          ) : null}
        </dl>

        {doc.notes || doc.terms ? (
          <div className="mt-5 space-y-4 border-t border-border pt-4">
            {doc.notes ? <TextBlock label={t("publicDoc.notes")} body={doc.notes} /> : null}
            {doc.terms ? <TextBlock label={t("publicDoc.terms")} body={doc.terms} /> : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
